import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import type { TeamMember } from "@/types/roster";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { teamOptions } from "@/lib/teams";
import {
  useCreateMember,
  useRoster,
  useUpdateMember,
  type RosterMemberPayload,
} from "@/hooks/useRoster";

const ROLE_OPTIONS: { key: string; label: string; role: string }[] = [
  { key: "pm", label: "Project Manager", role: "Project Manager" },
  { key: "ba", label: "Business Analyst", role: "Business Analyst" },
  { key: "functional", label: "Functional", role: "Functional" },
  { key: "technical", label: "Technical", role: "Technical" },
  { key: "developer", label: "Developer", role: "Developer" },
  { key: "infrastructure", label: "Infrastructure", role: "Infrastructure" },
  { key: "dba", label: "DBA", role: "DBA" },
  { key: "erp", label: "ERP", role: "ERP Consultant" },
  { key: "operations", label: "Operations", role: "Operations" },
];

interface Props {
  /** null = create mode, TeamMember = edit mode */
  member: TeamMember | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type FormState = {
  name: string;
  role_key: string;
  team: string;
  vendor: string;
  classification: string;
  rate_per_hour: string;
  weekly_hrs_available: string;
  support_reserve_pct: string;
  include_in_capacity: boolean;
};

function blank(): FormState {
  return {
    name: "",
    role_key: "developer",
    team: "",
    vendor: "",
    classification: "FTE",
    rate_per_hour: "0",
    weekly_hrs_available: "40",
    support_reserve_pct: "0",
    include_in_capacity: true,
  };
}

function fromMember(m: TeamMember): FormState {
  return {
    name: m.name,
    role_key: m.role_key,
    team: m.team ?? "",
    vendor: m.vendor ?? "",
    classification: m.classification ?? "FTE",
    rate_per_hour: m.rate_per_hour.toString(),
    weekly_hrs_available: m.weekly_hrs_available.toString(),
    // support_reserve_pct is 0..1 in DB, show as 0..100
    support_reserve_pct: Math.round(m.support_reserve_pct * 100).toString(),
    include_in_capacity: m.include_in_capacity,
  };
}

const NEW_TEAM = "\u0000new";

export function EditMemberDialog({ member, open, onOpenChange }: Props) {
  const isEdit = member !== null;
  const [form, setForm] = useState<FormState>(() =>
    member ? fromMember(member) : blank(),
  );
  const createMutation = useCreateMember();
  const updateMutation = useUpdateMember();
  const mutation = isEdit ? updateMutation : createMutation;

  // Existing teams across the current roster — drives the Team-field
  // datalist so picking a known team is a click, not a careful retype.
  // The roster query is already in cache (the parent page hit it on
  // mount); this just reads from that cache.
  const rosterQ = useRoster();
  const existingTeams = useMemo(
    () => teamOptions((rosterQ.data ?? []).map((m) => m.team)),
    [rosterQ.data],
  );
  // Typing a brand-new team name instead of picking one.
  const [newTeam, setNewTeam] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(member ? fromMember(member) : blank());
      setNewTeam(false);
      createMutation.reset();
      updateMutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, member?.name]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const canSubmit = form.name.trim() !== "";

  const handleSave = async () => {
    if (!canSubmit) return;
    const roleOption = ROLE_OPTIONS.find((r) => r.key === form.role_key);
    const payload: RosterMemberPayload = {
      name: form.name.trim(),
      role: roleOption?.role ?? form.role_key,
      role_key: form.role_key,
      team: form.team || null,
      vendor: form.vendor || null,
      classification: form.classification || null,
      rate_per_hour: Number.parseFloat(form.rate_per_hour) || 0,
      weekly_hrs_available: Number.parseFloat(form.weekly_hrs_available) || 0,
      support_reserve_pct: Math.max(
        0,
        Math.min(1, (Number.parseFloat(form.support_reserve_pct) || 0) / 100),
      ),
      include_in_capacity: form.include_in_capacity,
    };
    try {
      if (isEdit && member) {
        await updateMutation.mutateAsync({ originalName: member.name, payload });
      } else {
        await createMutation.mutateAsync(payload);
      }
      onOpenChange(false);
    } catch {
      /* error surfaces via mutation.error */
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit member" : "Add team member"}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Update this person's capacity, rate, or role."
              : "Add a new person to the team roster."}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-2">
          <Field label="Name">
            <input
              type="text"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              placeholder="Full name"
              disabled={isEdit}
              autoFocus={!isEdit}
              className="w-full rounded-md border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-subtle focus:border-navy-400 focus:outline-none focus:ring-2 focus:ring-navy-100 disabled:bg-canvas disabled:text-ink-muted"
            />
            {isEdit && (
              <div className="mt-1 text-[11px] text-ink-muted">
                Names are immutable — delete and recreate to rename.
              </div>
            )}
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Role">
              <select
                value={form.role_key}
                onChange={(e) => set("role_key", e.target.value)}
                className="w-full rounded-md border border-line bg-surface px-3 py-2 text-sm text-ink focus:border-navy-400 focus:outline-none focus:ring-2 focus:ring-navy-100"
              >
                {ROLE_OPTIONS.map((r) => (
                  <option key={r.key} value={r.key}>
                    {r.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Classification">
              <select
                value={form.classification}
                onChange={(e) => set("classification", e.target.value)}
                className="w-full rounded-md border border-line bg-surface px-3 py-2 text-sm text-ink focus:border-navy-400 focus:outline-none focus:ring-2 focus:ring-navy-100"
              >
                <option value="FTE">FTE</option>
                <option value="Contractor">Contractor</option>
                <option value="Vendor">Vendor</option>
              </select>
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Team">
              {/* Pick a team: the standard teams plus any others on the
                  roster. Free-text used to land "engineering" /
                  "Engineering" / "Eng" as separate buckets that didn't
                  roll up, so a new name is an explicit choice. */}
              {newTeam ? (
                <input
                  type="text"
                  autoFocus
                  value={form.team}
                  onChange={(e) => set("team", e.target.value)}
                  onBlur={() => !form.team.trim() && setNewTeam(false)}
                  placeholder="New team name"
                  aria-label="New team name"
                  className="w-full rounded-md border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-subtle focus:border-navy-400 focus:outline-none focus:ring-2 focus:ring-navy-100"
                />
              ) : (
                <select
                  value={form.team}
                  onChange={(e) => {
                    if (e.target.value === NEW_TEAM) {
                      set("team", "");
                      setNewTeam(true);
                    } else set("team", e.target.value);
                  }}
                  aria-label="Team"
                  className="w-full rounded-md border border-line bg-surface px-3 py-2 text-sm text-ink focus:border-navy-400 focus:outline-none focus:ring-2 focus:ring-navy-100"
                >
                  <option value="">No team</option>
                  {teamOptions([...existingTeams, form.team]).map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                  <option value={NEW_TEAM}>+ New team…</option>
                </select>
              )}
            </Field>
            <Field label="Vendor">
              <input
                type="text"
                value={form.vendor}
                onChange={(e) => set("vendor", e.target.value)}
                placeholder="Optional (for contractors)"
                className="w-full rounded-md border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-subtle focus:border-navy-400 focus:outline-none focus:ring-2 focus:ring-navy-100"
              />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Weekly hours">
              <NumberInput
                value={form.weekly_hrs_available}
                onChange={(v) => set("weekly_hrs_available", v)}
                min={0}
                max={80}
                suffix="hrs"
              />
            </Field>
            <Field label="Rate">
              <NumberInput
                value={form.rate_per_hour}
                onChange={(v) => set("rate_per_hour", v)}
                min={0}
                prefix="$"
              />
            </Field>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <Field label="Support %">
              <NumberInput
                value={form.support_reserve_pct}
                onChange={(v) => set("support_reserve_pct", v)}
                min={0}
                max={100}
                suffix="%"
              />
            </Field>
            <Field label="Project %">
              <div className="rounded-md border border-line bg-canvas px-3 py-2 text-sm tabular-nums text-ink-2">
                {Math.max(0, 100 - (Number.parseFloat(form.support_reserve_pct) || 0))}%
              </div>
            </Field>
            <Field label="Project hrs/wk">
              <div className="rounded-md border border-line bg-canvas px-3 py-2 text-sm tabular-nums text-ink-2">
                {(
                  (Number.parseFloat(form.weekly_hrs_available) || 0) *
                  (1 - Math.min(1, (Number.parseFloat(form.support_reserve_pct) || 0) / 100))
                ).toFixed(1)}
              </div>
            </Field>
          </div>

          <label className="flex items-start gap-3 rounded-md border border-line bg-canvas p-3">
            <input
              type="checkbox"
              checked={form.include_in_capacity}
              onChange={(e) => set("include_in_capacity", e.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0 accent-navy-900"
            />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-ink">
                Include in capacity calculations
              </div>
              <div className="mt-0.5 text-[11px] text-ink-muted">
                When unchecked, this person stays on the Team Roster for
                reference but their hours don't count toward role supply,
                utilization %, or the even-split demand fallback. Useful for
                leads or cross-functional roles who appear in the roster
                but don't actively take on work in their listed role.
              </div>
            </div>
          </label>

          {mutation.isError && (
            <div className="rounded-md border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 px-3 py-2 text-xs text-red-800 dark:text-red-300">
              Failed: {(mutation.error as Error).message}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={mutation.isPending || !canSubmit}
          >
            {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            {isEdit ? "Save" : "Add member"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block text-xs">
      <span className="mb-1 block font-semibold uppercase tracking-wider text-ink-muted">
        {label}
      </span>
      {children}
    </label>
  );
}

function NumberInput({
  value,
  onChange,
  min,
  max,
  prefix,
  suffix,
}: {
  value: string;
  onChange: (v: string) => void;
  min?: number;
  max?: number;
  prefix?: string;
  suffix?: string;
}) {
  return (
    <div className="relative">
      {prefix && (
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-ink-subtle">
          {prefix}
        </span>
      )}
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        min={min}
        max={max}
        className={`w-full rounded-md border border-line bg-surface py-2 text-sm tabular-nums text-ink focus:border-navy-400 focus:outline-none focus:ring-2 focus:ring-navy-100 ${
          prefix ? "pl-7" : "pl-3"
        } ${suffix ? "pr-10" : "pr-3"}`}
      />
      {suffix && (
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-ink-subtle">
          {suffix}
        </span>
      )}
    </div>
  );
}
